<template>
  <section>
    <sweet-modal ref="modal" :width="($mq.desktop) ? '500' : '100%'"
                 overlay-theme="dark"
                 blocking>
      <!-- Step 1 Enter phone number -->
      <section class="step-one" v-if="step === 1">
        <div class="text-center pb-3">
          <img src="/static/img/ic-phone-verification.svg" alt="" width="80">
        </div>
        <h3>{{ $t('message.verification.confirm_phone_number_title') }}</h3>
        <div class="row">
          <div class="col-md-12">
            {{ $t('message.verification.confirm_phone_number_detail') }}
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-8 offset-md-2 text-center">
            <label>{{ $t('message.verification.country') }}</label>
            <div class="select-jumbo">
              <select v-model="selectedCountryCode">
                <option :value="country.code" v-for="country in allCountries">
                  {{ country.name }}
                </option>
              </select>
            </div>
            <label class="mt-3">{{ $t('message.verification.phone_number') }}</label>
            <div class="input-group mb-3">
              <div class="input-group-prepend">
                <span class="input-group-text"
                      id="basic-addon1">+{{ allCountries[selectedCountryCode].dialCode }}</span>
              </div>
              <input type="tel"
                     class="form-control input-jumbo"
                     :placeholder="allCountries[selectedCountryCode].phoneFormat"
                     v-model="phoneNumber">
            </div>
            <div class="-status-alert text-remark mt-3" v-if="invalidPhone">
              {{ $t('message.verification.invalid_phone_number') }}
            </div>
            <div class="-status-alert text-remark mt-3" v-if="errorRegisteredPhoneNumber">
              <i class="fas fa-exclamation-triangle"></i>
              {{ $t('message.verification.registered_phone_number') }}
            </div>
            <div class="mt-4">
              <button class="button button-large button-block -cyan"
                      @click="validatePhoneNumber">
                <label>{{ $t('message.verification.confirm_phone_number') }}</label>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Step 2 Enter verification code -->
      <section class="step-two" v-else-if="step === 2">
        <div class="text-center pb-3">
          <img src="/static/img/ic-phone-verification.svg" alt="" width="80">
        </div>
        <h3>{{ $t('message.verification.enter_six_digits') }}</h3>
        <div class="row">
          <div class="col-md-8 offset-md-2">
            {{ $t('message.verification.we_sent_sms_to') }}
            {{ formattedPhoneNumber }}
            {{ $t('message.verification.please_fill_in_code') }}
          </div>
        </div>
       
        <div class="row py-3">
          <div class="col-12">
            <v-otp-input
              class="justify-content-center align-items-center"
              ref="otpInput"
              input-classes="otp-input"
              separator=""
              :num-inputs="6"
              :should-auto-focus="true"
              :is-input-num="true"
              @on-complete="handleOnComplete"
            />
          </div>
        </div>
         
        <div class="row">
          <div class="col-12 text-center">
            <div class="-status-alert text-remark mt-3" v-if="invalidCode">
              {{ errorMessages[errorIndex] }}
            </div>
            <div class="mt-3" v-if="isVerifying">
              <img src="/static/img/ring.gif" width="60">
            </div>
            <div class="mt-3" v-else>
              <div class="my-3">
                <a href="" @click.prevent="changePhoneNumber" class="btn btn-outline-secondary">
                  {{ $t('message.verification.change_phone_number') }}
                </a>
              </div>
              <div class="my-3">
                <a href="" @click.prevent="sendVerificationCode" class="btn btn-outline-info">
                  <i class="fal fa-redo"></i>
                  {{ $t('message.verification.resend_code') }}
                </a>
              </div>
            </div>
          </div>
          <div class="col-12">
            <hr>
          </div>
          <div class="col-12 text-center">
            <p>{{ $t('message.verification.verification_otp_alert') }}</p>
            <a href="https://line.me/R/ti/p/@BeNeat"
               target="_blank"
               class="btn btn-outline-info">{{ $t('message.subscription.contact_admin') }}</a>
          </div>
        </div>
      </section>
    </sweet-modal>

    <sweet-modal ref="verifiedModal" overlay-theme="dark" icon="success">
      {{ $t('message.verification.your_phone_number_is_verified') }}
      <div class="pt-4">
        <button class="btn btn-info btn-block" @click="closeVerifiedModal()">
          {{ $t('message.button.close') }}
        </button>
      </div>
    </sweet-modal>
  </section>
</template>

<script>
  import { parse, format, isValidNumber } from 'libphonenumber-js'
  import { mapGetters, mapState } from 'vuex'
  import { SweetModal } from 'sweet-modal-vue'
  import allCountries from '../../helpers/allCountries'
  import axios from 'axios'

  export default {
    components: {
      SweetModal
    },
    data () {
      return {
        errorIndex: 0,
        errorMessages: ['Code should contain only numbers', '', 'Verification code is expired'],
        allCountries,
        step: 1,
        selectedCountryCode: 'th',
        phoneNumber: '',
        formattedPhoneNumber: '',
        verificationCode: '',
        invalidPhone: false,
        invalidCode: false,
        errorRegisteredPhoneNumber: false,
        regNum: /^\d+$/,
        isVerifying: false
      }
    },
    computed: {
      ...mapGetters({
        state: 'phoneVerification/getState',
        user: 'auth/getUser'
      }),
      ...mapState('phoneVerification', ['isRegisteredPhoneNumber'])
    },
    watch: {
      isRegisteredPhoneNumber () {
        if (this.isRegisteredPhoneNumber === true) {
          this.errorRegisteredPhoneNumber = true
        } else if (this.isRegisteredPhoneNumber === false) {
          this.errorRegisteredPhoneNumber = false
          this.step = 2
        }
      }
    },
    methods: {
      openModal () {
        this.changePhoneNumber()
        this.$refs.modal.open()

        if (this.user) {
          this.phoneNumber = this.user.tel
        }
      },
      closeModal () {
        this.$refs.modal.close()
      },
      openVerifiedModal () {
        this.$refs.verifiedModal.open()
      },
      closeVerifiedModal () {
        this.$refs.verifiedModal.close()
      },
      async handleOnComplete (value) {
        if (this.regNum.test(value)) {
          const phoneNumber = this.formattedPhoneNumber.replace(/ /g, '')
          const submitData = {
            user_id: this.user.id,
            tel: phoneNumber,
            code: value
          }
          this.isVerifying = true
          const url = `phone-verification/${submitData.user_id}/verify`
          const { data } = await axios.post(url, submitData)
          this.isVerifying = false
          if (data) {
            switch (data.status) {
              case 0:
                this.displayInvalidCode(1)
                break
              case 1:
                // Update tel and isPhoneVerified
                this.$store.commit('auth/UPDATE_USER_DATA', { key: 'tel', value: phoneNumber })
                this.$store.commit('auth/UPDATE_USER_DATA', { key: 'isPhoneVerified', value: true })
                this.closeModal()
                this.openVerifiedModal()
                break
              case 2:
                this.displayInvalidCode(2)
                break
              case null:
                this.invalidCode = false
                break
            }
          }
        } else {
          this.displayInvalidCode(0)
        }
      },
      validatePhoneNumber () {
        const parsedResult = parse(this.phoneNumber, this.selectedCountryCode.toUpperCase())
        this.formattedPhoneNumber = format(parsedResult.phone, parsedResult.country, 'International')
        if (isValidNumber(this.formattedPhoneNumber)) {
          this.invalidPhone = false
          this.sendVerificationCode()
        } else {
          this.invalidPhone = true
        }
      },
      sendVerificationCode () {
        const phone = this.formattedPhoneNumber.replace(/ /g, '')
        const submitData = {
          user_id: this.user.id,
          tel: phone
        }
        this.$store.dispatch('phoneVerification/GET_VERIFICATION_CODE', submitData)
      },
      changePhoneNumber () {
        this.step = 1
        this.phoneNumber = ''
        this.formattedPhoneNumber = ''
        this.errorIndex = 0
        this.selectedCountryCode = 'th'
        this.invalidPhone = false
        this.invalidCode = false
        if (this.$refs.otpInput) {
          this.$refs.otpInput.clearInput()
        }
      },
      displayInvalidCode (index) {
        this.errorIndex = index
        this.invalidCode = true
      }
    },
    mounted () {
      this.step = 1
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/modal/PhoneVerificationModal.vue